import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LocalAtmRoundedIcon from '@mui/icons-material/LocalAtmRounded';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TuneIcon from '@mui/icons-material/Tune';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WebIcon from '@mui/icons-material/Web';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import { AppPages } from '@trnsact/trnsact-shared-types';
import { APP_PAGES_LABELS, APP_PAGES_ORDER, ROLE_COLUMNS } from '../constants';
import { useUserAccessTable } from '../hooks/useUserAccessTable';

const PAGE_ICONS: Record<AppPages, React.ReactElement> = {
  [AppPages.CompanyProfile]: <BusinessIcon fontSize="small" />,
  [AppPages.UserManagement]: <SupervisorAccountIcon fontSize="small" />,
  [AppPages.LenderManagement]: <PersonAddIcon fontSize="small" />,
  [AppPages.FinancePrograms]: <LocalAtmRoundedIcon fontSize="small" />,
  [AppPages.LocationManagement]: <LocationOnIcon fontSize="small" />,
  [AppPages.PortalSetup]: <TuneIcon fontSize="small" />,
  [AppPages.AftermarketProducts]: <StorefrontIcon fontSize="small" />,
  [AppPages.Inventory]: <LocalShippingIcon fontSize="small" />,
  [AppPages.WebsiteIntegrations]: <WebIcon fontSize="small" />,
  [AppPages.PrescreenTools]: <FormatListNumberedIcon fontSize="small" />,
};

export const UserAccessTable = () => {
  const { isLoading, isSaving, isChecked, isDisabledByPcw, handleChange, handleSave } = useUserAccessTable();

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height={200}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 0 }}>
        <Typography variant="h6" component="span" gutterBottom>
          User access
        </Typography>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f7f8fa' }}>
                <TableCell sx={{ width: 220, fontWeight: 600, color: 'text.secondary' }}>
                  Admin sidebar section
                </TableCell>
                {ROLE_COLUMNS.map(({ role, label }) => (
                  <TableCell key={role} align="center" sx={{ fontWeight: 600, minWidth: 90, py: 2 }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {APP_PAGES_ORDER.map(page => (
                <TableRow key={page} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {PAGE_ICONS[page]}
                      <Typography variant="body2">{APP_PAGES_LABELS[page]}</Typography>
                    </Stack>
                  </TableCell>
                  {ROLE_COLUMNS.map(({ role }) => {
                    const disabled = isDisabledByPcw(page, role);

                    return (
                      <TableCell key={role} align="center">
                        <Tooltip title={disabled ? 'Disabled by TRNSACT admin' : ''} arrow>
                          <span>
                            <Checkbox
                              checked={isChecked(page, role)}
                              disabled={disabled}
                              onChange={e => handleChange(page, role, e.target.checked)}
                              sx={{ p: 0.5 }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button variant="contained" disabled={isSaving} onClick={handleSave}>
          Save
        </Button>
      </CardActions>
    </Card>
  );
};
